import { Message } from '@patsnap/tahiti-core/common/message';
import { isPsHttpError } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { I18nService } from '@telecom/shared/services/i18n';
import type { ErrorHandlerContribution, ErrorHandlerRegistry } from '@patsnap/tahiti-core';
import { SSE_ERROR_MESSAGE_FIRST_RESPONSE_TIMEOUT, SSE_ERROR_MESSAGE_PROGRESS_TIMEOUT } from '@/services/sse-ps-http/constants';
import { isToastError, isToastWarning } from '@telecom/shared/utils/toast-error';
import type { AxiosError } from 'axios';
import { CommandService } from '@patsnap/tahiti-core';
import { UserInfoService } from '@telecom/shared/domain/permission/services/user-info-service';
import { HubspotDialogService } from '@/services/hubspot/hubspot-dialog.service';
import type { HubspotDialogService as HubspotDialogServiceType } from '@/services/hubspot/hubspot-dialog.service';
import { getIsOversea } from '@telecom/shared/utils/environment';

/**
 * 创建任务时 用户的输入不能为空
 */
const ERR_CODE_CREATE_TASK_INPUT_EMPTY = 33120006;

/**
 * 积分超限
 */
const ERR_CODE_CREDIT_OVER_LIMIT = 33100006;

/**
 * 敏感词
 */
const ERR_CODE_SENSITIVE_WORD = 33120007;

/**
 * 没有访问该任务的权限
 */
const ERR_CODE_NO_ACCESS_PERMISSION = 33110004;

/**
 * 次数超限
 */
const ERR_CODE_COUNT_OVER_LIMIT = 33100007;

/**
 * FTO, 添加专利失败
 */
const FTO_ADD_PATENT_INVALID = 33120014;

/**
 * 输入过长
 */
const ERR_CODE_INPUT_TOO_LONG = 33120009;
/**
 * 说明书提取线框图次数超限
 */
const ERR_CODE_SPECIFICATION_WIRE_FRAME_COUNT_OVER_LIMIT = 33110007;
/**
 * 任务已删除
 */
const ERR_CODE_TASK_DELETED = 33110014;
/**
 * 游客版任务触发上限
 */
const GUEST_USAGE_LIMIT = 33110015;

interface ErrorConfig {
    code: number;
    i18nKey: string;
}

const ERROR_CONFIGS: ErrorConfig[] = [
    { code: ERR_CODE_CREATE_TASK_INPUT_EMPTY, i18nKey: 'drafting.errors.inputEmpty' },
    { code: ERR_CODE_SENSITIVE_WORD, i18nKey: 'aiTranslation.sensitiveTip' },
    { code: FTO_ADD_PATENT_INVALID, i18nKey: 'ftoReport.errors.addInvalidPatent' },
    { code: ERR_CODE_NO_ACCESS_PERMISSION, i18nKey: 'drafting.errors.noAccessPermission' },
    { code: ERR_CODE_INPUT_TOO_LONG, i18nKey: 'drafting.errors.inputTooLong' },
    { code: ERR_CODE_SPECIFICATION_WIRE_FRAME_COUNT_OVER_LIMIT, i18nKey: 'drafting.errors.specificationWireFrameCountOverLimit' },
    { code: ERR_CODE_TASK_DELETED, i18nKey: 'drafting.errors.taskDeleted' },
];

/**
 * unknown error messages
 */
const UNKNOWN_ERROR_MESSAGES: Record<I18nService['locale']['value'], string> = {
    cn: '服务器异常，请稍后重试',
    en: 'Server error, please try again later',
    tw: '伺服器出錯，請稍後再試',
    jp: 'サーバーが異常です。少し後で再試してください。',
};

const ENTERPRISE_COMMERCIAL_USAGE_LIMIT_MESSAGES: Record<I18nService['locale']['value'], string> = {
    cn: '使用额度已达上限，请联系管理员获取更多额度。',
    en: 'Your usage limit has been reached. Contact your admin to get more access.',
    jp: '利用上限に達しました。追加の利用枠については管理者にお問い合わせください。',
    tw: '使用額度已達上限，請聯絡管理員取得更多額度。',
};

@injectable()
export class DraftingErrorHandler implements ErrorHandlerContribution {
    constructor(
        @inject(Message)
        private readonly _message: Message,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
        @inject(CommandService)
        private readonly _commandService: CommandService,
        @inject(UserInfoService)
        private readonly _userInfoService: UserInfoService,
        @inject(HubspotDialogService)
        private readonly _hubspotDialogService: HubspotDialogServiceType,
    ) { }

    private _handleError(error: Error, errorConfig: ErrorConfig): boolean {
        if (isPsHttpError(error)) {
            const data = error.cause;
            if (data.error_code === errorConfig.code) {
                this._message.error({
                    message: this._i18nService.t(errorConfig.i18nKey),
                });
                return true;
            }
        }
        return false;
    }

    registerHandler(handlers: ErrorHandlerRegistry): void {
        ERROR_CONFIGS.forEach(config => {
            handlers.register((error: Error) => this._handleError(error, config));
        });
        this._handleUsageLimitError(handlers);
        this._handleGuestUsageLimitError(handlers);
        this._handlePermissionError(handlers);
        this._handleSseTimeoutError(handlers);
        this._handleToastError(handlers);
        this._handleToastWarning(handlers);
        this._handleFallbackError(handlers);
    }

    /**
     * 处理 使用量超限错误
     */
    private _handleUsageLimitError(handlers: ErrorHandlerRegistry): void {
        handlers.register((error: any) => {
            if (!this._isUsageLimitError(error)) return false;
            if (this._tryHandleEnterpriseCommercialUsageLimit()) return true;
            this._handleCountOverLimit();
            return true;
        });
    }

    private _isUsageLimitError(error: any): boolean {
        return this._matchErrorCode(error, ERR_CODE_COUNT_OVER_LIMIT)
            || this._matchErrorCode(error, ERR_CODE_CREDIT_OVER_LIMIT);
    }

    private _tryHandleEnterpriseCommercialUsageLimit(): boolean {
        if (!this._userInfoService.isEnterpriseCommercialUser()) return false;
        this._message.error({
            message: this._getEnterpriseCommercialUsageLimitMessage(),
        });
        return true;
    }

    private _getEnterpriseCommercialUsageLimitMessage(): string {
        return ENTERPRISE_COMMERCIAL_USAGE_LIMIT_MESSAGES[this._i18nService.locale.value];
    }

    private _handleCountOverLimit(): void {
        if (!getIsOversea()) {
            this._commandService.executeCommand('trial-end:open-dialog');
            return;
        }
        if (this._userInfoService.isEnterpriseTenant()) {
            this._message.warning({
                message: this._i18nService.t('drafting.errors.usageLimitExceeded.contactCustomerService'),
            });
            this._hubspotDialogService.openContactUsDialog();
        } else {
            this._commandService.executeCommand('usage-limit:navigate-to-pricing', {
                trigger_source: 'paywall',
                header_type: 'credits_limit_upgrade',
            });
        }
    }

    /**
     * 处理游客版使用超限错误
     */
    private _handleGuestUsageLimitError(handlers: ErrorHandlerRegistry): void {
        handlers.register((error: any) => {
            if (!this._matchErrorCode(error, GUEST_USAGE_LIMIT)) return false;
            this._commandService.executeCommand('login:open-dialog', {
                trigger_source: 'usage_limit',
            });
            return true;
        });
    }

    private _handlePermissionError(handlers: ErrorHandlerRegistry): void {
        handlers.register((error) => {
            if (this._isAxiosError(error) && error.response?.status === 403) {
                this._message.error({
                    message: this._i18nService.t('drafting.errors.noAccessPermission'),
                });
                return true;
            }
            return false;
        });
    }

    private _handleToastError(handlers: ErrorHandlerRegistry): void {
        handlers.register((error: Error) => {
            if (isToastError(error)) {
                this._message.error({
                    message: error.message,
                });
                return true;
            }
            return false;
        });
    }

    private _handleToastWarning(handlers: ErrorHandlerRegistry): void {
        handlers.register((error: Error) => {
            if (isToastWarning(error)) {
                this._message.warning({
                    message: error.message,
                });
                return true;
            }
            return false;
        });
    }

    /**
     * 兜底错误处理
     */
    private _handleFallbackError(handlers: ErrorHandlerRegistry): void {
        handlers.register((error) => {
            console.error(error);
            const language = this._i18nService.locale.value;
            if (isPsHttpError(error)) {
                this._message.error({
                    message: UNKNOWN_ERROR_MESSAGES[language],
                });
                return true;
            }
            // if (this._isAxiosError(error)) {
            //     this._message.error({
            //         message: UNKNOWN_ERROR_MESSAGES[language],
            //     });
            //     return true;
            // }
            return false;
        });
    }

    /**
     * 处理 SSE 超时错误
     * @param error
     * @returns
     */
    private _handleSseTimeoutError(handlers: ErrorHandlerRegistry) {
        handlers.register((error: Error) => {
            if (error?.message === SSE_ERROR_MESSAGE_FIRST_RESPONSE_TIMEOUT) {
                this._message.error({
                    message: this._i18nService.t('sse.firstResponseTimeout'),
                });
                return true;
            }
            if (error?.message === SSE_ERROR_MESSAGE_PROGRESS_TIMEOUT) {
                this._message.error({
                    message: this._i18nService.t('sse.progressTimeout'),
                });
                return true;
            }
            return false;
        });
    }

    /**
     * 匹配错误码，兼容 AxiosError 和 PsHttpError 两种格式
     */
    private _matchErrorCode(error: any, code: number): boolean {
        if (Number(error?.response?.data?.error_code) === code) return true;
        if (error?.response?.data?.numeric_error_code === code) return true;
        if (isPsHttpError(error) && error.cause?.error_code === code) return true;
        return false;
    }

    /**
     * 类型守卫函数：判断错误是否为 AxiosError
     */
    private _isAxiosError(error: unknown): error is AxiosError {
        return (error as any)?.isAxiosError;
    }
}
